import {
  useCompany,
  useDealer,
  useLinkDealerToContractType,
  useUnlinkDealerFromContractType,
  useContractTypes,
  useUsers,
} from '@api'
import {FormFieldType, NavigationPathKey, TranslationKey} from '@enums'
import {GridColDef} from '@mui/x-data-grid'
import {errorAtom, getUserTimeFormatAtom, pageLinksAtom, successMessageAtom} from '@state'

import {useEffect, useMemo, useState} from 'react'

import {
  activeCell,
  FormFieldItem,
  generateDropdownOptions,
  generateReadableErrorMessages,
  isCompanyUser,
  roles,
  unlinkButtonCell,
  renderClickableCell,
  useViewNavigate,
  getUserData,
} from 'common'
import {formatDate} from 'date-fns'
import {useAtomValue, useSetAtom} from 'jotai'
import {useTranslation} from 'react-i18next'
import {useParams} from 'react-router-dom'

import {ComposedComponents, UIKit} from '@components'

export interface OfficialSignatoriesRow {
  name: string
  email: string
  dateOfBirth: string
  personalNumber: string
}

export enum DealerTab {
  GENERAL,
  CONTRACT_TYPES,
  USERS,
}

export function useDealerViewModel() {
  const setError = useSetAtom(errorAtom)
  const setSuccessMessage = useSetAtom(successMessageAtom)
  const setTitleLinks = useSetAtom(pageLinksAtom)
  const [activeTab, setActiveTab] = useState<DealerTab>(DealerTab.GENERAL)
  const [isLinkModalShown, setIsLinkModalShown] = useState<boolean>(false)
  const [contractTypeToLink, setContractTypeToLink] = useState<string | undefined>(undefined)
  const [contractTypeFormFieldError, setContractTypeFormFieldError] = useState<string>('')
  const isEditButtonVisible = !isCompanyUser()
  const navigate = useViewNavigate()
  const {id} = useParams()
  const {t} = useTranslation()

  const currentUser = getUserData()
  const getUserTimeFormat = useAtomValue(getUserTimeFormatAtom)
  const userTimeFormat = getUserTimeFormat(currentUser.id)

  const {data: dealerData} = useDealer(id)
  const {data: contractTypesData} = useContractTypes()
  const {data: usersData} = useUsers()
  const {data: companyData} = useCompany(dealerData?.companyId)

  const userRows = useMemo(() => {
    if (!usersData) return []
    else
      return usersData
        .filter(item => item.dealerId === id)
        .map(item => ({
          name: item.fullName,
          email: item.email,
          active: item.active,
          company: '',
          dealer: '',
          role: roles(t).find(roleItem => roleItem.code === item.role)?.name || '',
          id: item.id,
        }))
  }, [usersData])

  const contractTypeOptions = useMemo(() => {
    if (!contractTypesData) return []
    else
      return generateDropdownOptions(
        contractTypesData.filter(
          item => !dealerData?.allowedContractTypes?.map(contractType => contractType.id).includes(item.id),
        ),
      )
  }, [contractTypesData, dealerData])

  const contractTypeRows = useMemo(() => {
    if (!dealerData) return []
    else
      return dealerData?.allowedContractTypes.map(item => ({
        name: item.name,
        id: item.id,
        unlinkId: item.id,
      }))
  }, [dealerData])

  const {mutate: linkDealerToContractTypeMutation} = useLinkDealerToContractType()
  const {mutate: unlinkDealerFromContractTypeMutation} = useUnlinkDealerFromContractType()

  useEffect(
    () =>
      setTitleLinks([
        {title: t(TranslationKey['Admin panel']), link: NavigationPathKey.ADMIN_PANEL},
        {title: t(TranslationKey.Resources), link: NavigationPathKey.RESOURCES_DASHBOARD},
        {
          title: t(TranslationKey['Dealer search']),
          link: NavigationPathKey.DEALER_SEARCH,
        },
        {title: t(TranslationKey['Dealer view'])},
      ]),
    [],
  )

  /**
   * Dynamically generated modal text
   */
  const modalText: string = useMemo(
    () => `${t(TranslationKey['Choose a contract type to link to'])} ${dealerData?.name}`,
    [dealerData?.name],
  )

  /**
   * User table columns configuration
   */
  const userColumns: GridColDef[] = [
    {
      field: 'name',
      headerName: t(TranslationKey.Name),
      flex: 0.8,
      hideable: false,
      renderCell: params =>
        renderClickableCell(params, () => navigate(`${NavigationPathKey.USER_VIEW}/${params.row.id}`)),
    },
    {field: 'email', headerName: t(TranslationKey.Email), flex: 1},
    {field: 'role', headerName: t(TranslationKey.Role), flex: 0.8},
    activeCell(t),
  ]

  /**
   * Contract Type table columns configuration
   */
  const contractTypeColumns: GridColDef[] = [
    {
      field: 'name',
      headerName: t(TranslationKey.Name),
      flex: 1.5,
      hideable: false,
      renderCell: params =>
        renderClickableCell(params, () => navigate(`${NavigationPathKey.CONTRACT_TYPE_VIEW}/${params.row.id}`)),
    },
    ...(isEditButtonVisible ? [unlinkButtonCell(t, id => onUnlink(id))] : []),
  ]

  /**
   * Official Signatory table columns configuration
   */
  const officialSignatoriesColumns: GridColDef[] = [
    {
      field: 'name',
      headerName: t(TranslationKey.Name),
      flex: 1,
    },
    {
      field: 'email',
      headerName: t(TranslationKey.Email),
      flex: 1.2,
    },
    {
      field: 'dateOfBirth',
      headerName: t(TranslationKey['Date of birth']),
      flex: 1,
      renderCell: params => (params.value ? formatDate(params.value, userTimeFormat) : '-'),
    },
    {
      field: 'personalNumber',
      headerName: t(TranslationKey['Personal number']),
      flex: 1,
    },
  ]

  /**
   * Tab buttons configuration
   */
  const tabButtons: ComposedComponents.TabButtonProps<DealerTab>[] = [
    {
      label: t(TranslationKey['General information']),
      key: DealerTab.GENERAL,
      onClick: () => setActiveTab(DealerTab.GENERAL),
    },
    {
      label: t(TranslationKey['Contract types']),
      key: DealerTab.CONTRACT_TYPES,
      onClick: () => setActiveTab(DealerTab.CONTRACT_TYPES),
    },
    {
      label: t(TranslationKey.Users),
      key: DealerTab.USERS,
      onClick: () => setActiveTab(DealerTab.USERS),
    },
  ]

  /**
   * Official Signatories rows data
   */
  const officialSignatoriesRows: OfficialSignatoriesRow[] = dealerData?.officialSignatories || []

  /**
   * Linked Contract Type form field configuration
   */
  const linkContractTypeFormField: FormFieldItem = {
    fieldType: FormFieldType.SEARCHABLE_DROPDOWN,
    items: contractTypeOptions,
    value: contractTypeToLink,
    onChange: (item: UIKit.DropdownItem | null) => {
      setContractTypeToLink(String(item?.value))
      setContractTypeFormFieldError('')
    },
    error: !!contractTypeFormFieldError,
    placeholder: t(TranslationKey['Choose a contract type to link to']),
    listboxHeight: 100,
  }

  /**
   * Dealer form fields configuration - general section
   */
  const dealerFormFields: FormFieldItem[] = [
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Dealer name']),
      text: dealerData?.name || '-',
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Dealer number']),
      text: dealerData?.dealerNo || '-',
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['VAT number']),
      text: dealerData?.vatNumber || '-',
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey.Company),
      text: companyData?.name,
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Company number']),
      text: dealerData?.companyNo || '-',
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Customer number']),
      text: dealerData?.customerNo || '-',
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey.Email),
      text: dealerData?.email || '-',
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey.Phone),
      text: dealerData?.phone || '-',
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Active']),
      text: dealerData?.active ? t(TranslationKey.Active) : t(TranslationKey.Inactive),
    },
  ]

  /**
   * Dealer form fields configuration - contact section
   */
  const dealerContactFormFields = [
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Invoice email']),
      text: dealerData?.invoiceEmail || '-',
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Invoice phone']),
      text: dealerData?.invoicePhone || '-',
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey['Zip code']),
      text: dealerData?.zipCode || '-',
    },
    {
      fieldType: FormFieldType.LABEL,
      label: t(TranslationKey.City),
      text: dealerData?.city || '-',
    },
  ]

  /**
   * A function handling linking the Dealer to a Contract Type
   * @param contractTypeId Contract Type ID
   */
  const onUnlink = (contractTypeId: string) => {
    unlinkDealerFromContractTypeMutation(
      {dealerId: String(id), contractTypeId},
      {
        onSuccess: () => {
          setSuccessMessage(t(TranslationKey['Successfully unlinked a contract type']))
        },
      },
    )
  }

  /**
   * A function handling linking the Dealer to a Contract Type
   * @returns void
   */
  const onLink = () => {
    if (!contractTypeToLink) {
      setContractTypeFormFieldError('contractTypeToLink')
      setError({
        message: generateReadableErrorMessages(t, ['contractTypeToLink']),
        name: '',
      })
      return
    }

    setIsLinkModalShown(false)
    setContractTypeToLink(undefined)
    if (id)
      linkDealerToContractTypeMutation(
        {contractTypeId: contractTypeToLink, dealerId: id},
        {
          onSuccess: () => {
            setSuccessMessage(t(TranslationKey['Successfully linked a contract type']))
          },
        },
      )
  }

  /**
   * A function handling the Back button clikc
   */
  const onPressBack = (): void => {
    navigate(NavigationPathKey.DEALER_SEARCH)
  }

  /**
   * A function handling the Edit button click
   */
  const onPressEdit = (): void => {
    navigate(`${NavigationPathKey.DEALER_CREATION}/${id}`)
  }

  /**
   * A function handling the modal close
   */
  const onCloseLinkModal = () => {
    setIsLinkModalShown(false)
    setContractTypeToLink(undefined)
    setContractTypeFormFieldError('')
  }

  /**
   * Create button should be available only for Administrator users
   */
  const showCreateButton = !isCompanyUser()

  return {
    navigate,
    onPressEdit,
    onPressBack,
    onCloseLinkModal,
    setIsLinkModalShown,
    onLink,
    id,
    showCreateButton,
    linkContractTypeFormField,
    modalText,
    isLinkModalShown,
    t,
    isEditButtonVisible,
    dealerFormFields,
    dealerContactFormFields,
    officialSignatoriesColumns,
    officialSignatoriesRows,
    userColumns,
    contractTypeColumns,
    contractTypeOptions,
    contractTypeRows,
    userRows,
    activeTab,
    tabButtons,
  }
}
